// Chips.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import "./Chips.scss";

const Chips = ({
  data = [],
  selectedChips = [],
  onSelectionChange = () => {},
  onCreate = () => {},
  onRemove = () => {},
  allowCreate = false,
  allowRemove = true,
  allowSuggestion = true,
  disabled = false,
  maxLength = 20,
  maxHeight = null,
  defaultBorderColor = "#d1d5db",
  defaultTextColor = "#374151",
  selectedBorderColor = "#3b82f6",
  selectedTextColor = "#ffffff",
  selectedBackgroundColor = "#3b82f6",
  placeholder = "Type to search or create...",
  className = "",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const normalizedData = useMemo(() => {
    return data.map((item) => {
      if (typeof item === "string") {
        return {
          name: item,
          disabled: false,
          borderColor: defaultBorderColor,
          textColor: defaultTextColor,
        };
      }
      return {
        name: item.name,
        disabled: item.disabled || false,
        borderColor: item.borderColor || defaultBorderColor,
        textColor: item.textColor || defaultTextColor,
      };
    });
  }, [data, defaultBorderColor, defaultTextColor]);

  //const normalizedData = normalizeData(data);

  useEffect(() => {
    if (inputValue.trim() && allowSuggestion) {
      const suggestions = normalizedData.filter(
        (item) =>
          item.name.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedChips.includes(item.name) &&
          !item.disabled
      );
      setFilteredSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, normalizedData, selectedChips, allowSuggestion]);

  const getChipData = (chipName) => {
    return (
      normalizedData.find((item) => item.name === chipName) || {
        name: chipName,
        disabled: false,
        borderColor: defaultBorderColor,
        textColor: defaultTextColor,
      }
    );
  };

  const handleChipClick = (chip) => {
    if (disabled) return;
    const chipData = getChipData(chip);
    if (chipData.disabled) return;
    const isSelected = selectedChips.includes(chip);
    const newSelection = isSelected
      ? selectedChips.filter((item) => item !== chip)
      : [...selectedChips, chip];
    onSelectionChange(newSelection);
  };

  const handleRemoveChip = (chipToRemove, isFromData = false) => {
    if (disabled) return;
    if (isFromData && allowRemove) {
      onRemove(chipToRemove);
    }
    if (selectedChips.includes(chipToRemove)) {
      const newSelection = selectedChips.filter(
        (item) => item !== chipToRemove
      );
      onSelectionChange(newSelection);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (disabled) return;
    if (!selectedChips.includes(suggestion.name)) {
      onSelectionChange([...selectedChips, suggestion.name]);
    }
    setInputValue("");
    setShowSuggestions(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" && inputValue.trim() && allowCreate) {
      const newChip = inputValue.trim();
      if (!normalizedData.find((item) => item.name === newChip)) {
        onCreate(newChip);
        if (!selectedChips.includes(newChip)) {
          onSelectionChange([...selectedChips, newChip]);
        }
      } else {
        if (!selectedChips.includes(newChip)) {
          onSelectionChange([...selectedChips, newChip]);
        }
      }
      setInputValue("");
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    if (allowSuggestion && inputValue.trim()) {
      setShowSuggestions(filteredSuggestions.length > 0);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const truncateText = (text, maxLen) => {
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen);
  };

  const getDisplayText = (text) => {
    return text.length <= maxLength
      ? text
      : truncateText(text, maxLength) + "...";
  };

  const containerMaxHeight = maxHeight ? `${maxHeight}rem` : "auto";

  return (
    <div
      className={`chips-container ${className}`}
      style={{
        "--selected-bg-color": selectedBackgroundColor,
        "--selected-text-color": selectedTextColor,
        "--selected-border-color": selectedBorderColor,
        "--max-height": containerMaxHeight,
      }}
    >
      <div
        className={`chips-wrapper ${disabled ? "disabled" : ""} ${
          maxHeight ? "chips-scrollable" : ""
        }`}
      >
        {selectedChips.map((chip, index) => {
          const chipData = getChipData(chip);
          return (
            <div
              key={`selected-${index}`}
              className={`chip selected ${disabled ? "disabled" : ""} ${
                chipData.disabled ? "individual-disabled" : ""
              }`}
              onClick={() => handleChipClick(chip)}
              title={chip.length > maxLength ? chip : undefined}
            >
              <span className="chip-text">{getDisplayText(chip)}</span>
              {allowRemove && !chipData.disabled && (
                <button
                  className={`remove-btn ${disabled ? "disabled" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveChip(chip, true);
                  }}
                  title="Remove chip"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}

        {normalizedData
          .filter((item) => !selectedChips.includes(item.name))
          .map((item, index) => (
            <div
              key={`available-${index}`}
              className={`chip ${disabled ? "disabled" : ""} ${
                item.disabled ? "individual-disabled" : ""
              }`}
              style={{ borderColor: item.borderColor, color: item.textColor }}
              onClick={() => handleChipClick(item.name)}
              title={item.name.length > maxLength ? item.name : undefined}
            >
              <span className="chip-text">{getDisplayText(item.name)}</span>
              {allowRemove && !item.disabled && (
                <button
                  className={`remove-btn ${disabled ? "disabled" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveChip(item.name, true);
                  }}
                  title="Remove chip"
                >
                  ×
                </button>
              )}
            </div>
          ))}

        {allowCreate && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            className="chip-input"
          />
        )}
      </div>

      {showSuggestions && allowSuggestion && (
        <div className="suggestions-container">
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={`suggestion-${index}`}
              className="suggestion-item"
              onMouseDown={() => handleSuggestionClick(suggestion)}
              title={
                suggestion.name.length > maxLength ? suggestion.name : undefined
              }
            >
              {getDisplayText(suggestion.name)}
            </div>
          ))}
          {allowCreate &&
            inputValue &&
            !normalizedData.find((item) => item.name === inputValue.trim()) && (
              <div
                className="suggestion-item create-suggestion"
                onClick={() => {
                  if (disabled) return;
                  const newChip = inputValue.trim();
                  onCreate(newChip);
                  onSelectionChange([...selectedChips, newChip]);
                  setInputValue("");
                  setShowSuggestions(false);
                }}
              >
                Create "{getDisplayText(inputValue.trim())}"
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default Chips;
